import { useCallback, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

const THEME_KEY = 'cue-app-theme';
const THEME_CHANGE_EVENT = 'cue-theme-change';

const getStoredTheme = (): Theme => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  try {
    return window.localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

export const applyTheme = (theme: Theme) => {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
    root.setAttribute('data-cue-preview-theme', 'dark');
  } else {
    root.classList.remove('dark');
    root.removeAttribute('data-cue-preview-theme');
  }
};

export const applyStoredTheme = () => {
  applyTheme(getStoredTheme());
};

export const useTheme = () => {
  const [theme, setTheme] = useState<Theme>(getStoredTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const onThemeChange = (e: Event) => {
      setTheme((e as CustomEvent<Theme>).detail);
    };

    const onStorageChange = (e: StorageEvent) => {
      if (e.key === THEME_KEY) {
        setTheme(e.newValue === 'dark' ? 'dark' : 'light');
      }
    };

    window.addEventListener(THEME_CHANGE_EVENT, onThemeChange);
    window.addEventListener('storage', onStorageChange);
    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, onThemeChange);
      window.removeEventListener('storage', onStorageChange);
    };
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next: Theme = prev === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem(THEME_KEY, next);
        window.dispatchEvent(
          new CustomEvent<Theme>(THEME_CHANGE_EVENT, { detail: next }),
        );
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  return { theme, isDark: theme === 'dark', toggleTheme };
};
